import { action, computed, observable, makeAutoObservable } from 'mobx';
import { FormModel } from 'src_common/common/mobx-utils/Form2/FormModel';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { validateNotEmpty, validateHexColor, validateExpectLink } from 'src_common/common/mobx-utils/Form2/validators';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { apiCommon } from 'src/api/ApiCommon';
import { UploadImageState } from 'src/AppState/UploadImageState/UploadImageState';
import * as t from 'io-ts';
import { HeadlineCarouselModelType } from 'src/api/config/headlineCarousel/getHeadlineCarousel';
import { CollectionType } from 'src/main/cms/HeadlineCarousel/HeadlineCarousel.state';

const SelectionsDetailsIO = t.interface({
    id: t.number,
    label: t.string,
});

export enum RedirectTypeEnum {
    REDIRECT_NONE = 'NONE',
    REDIRECT_URL = 'REDIRECT_URL',
    REDIRECT_GAME = 'REDIRECT_GAME',
    REDIRECT_CATEGORY_CASINO = 'REDIRECT_CATEGORY_CASINO',
    REDIRECT_CATEGORY_LIVE_CASINO = 'REDIRECT_CATEGORY_LIVE_CASINO',
}

export type SelectionsDetailsType = t.TypeOf<typeof SelectionsDetailsIO>;

type FormType = {
    date_to: string;
    date_from: string;
    title: string;
    subtitle: string;
    is_active: boolean;
    btn_color: string;
    btn_title: string;
    tc_text: string;
    action_template: string;
    game_id: string | null;
    direct_link: string;
    category_id: string | null;
};

export type HeadlineCarouselType = {
    id: number;
    btn_color?: string | undefined | null;
    btn_title?: string | undefined | null;
    bg_color?: string | undefined | null;
    display_order: number;
    img_url_mobile?: string | undefined | null;
    img_url_tablet?: string | undefined | null;
    img_url_desktop?: string | undefined | null;
    date_from: string;
    date_to: string;
    tc_text?: string | undefined | null;
    action_template: string;
    game_id?: string | undefined | null;
    direct_link?: string | undefined | null;
    category_id?: string | undefined | null;
    title: string;
    subtitle: string;
    is_active: boolean;
};

interface HeadlineCarouselLoading {
    type: 'loading';
}
interface HeadlineCarouselReady {
    type: 'ready';
    value: Array<HeadlineCarouselModelType>;
}
interface HeadlineCarouselError {
    type: 'error';
    value: string;
}

type HeadlineCarouselContentResponse = HeadlineCarouselLoading | HeadlineCarouselReady | HeadlineCarouselError;

interface RedirectOptionsType {
    key: string;
    name: string;
}

export class HeadlineCarouselDataState {
    @observable private readonly headlineCarouselResource: Resource<Array<HeadlineCarouselModelType>>;
    @observable public isEditMode: boolean = false;
    @observable private singleCarousel: HeadlineCarouselType | null = null;
    @observable public errorMessageForList: string | null = null;
    @observable public redirectionError: string | null = null;
    @observable public imagesError: string | null = null;

    public isPublished: FormInputState<boolean, boolean>;
    public title: FormInputState<string, string>;
    public btnColor: FormInputState<string, string>;
    public btnTitle: FormInputState<string, string>;
    public bgColor: FormInputState<string, string>;
    public subtitle: FormInputState<string, string>;
    public dateStart: FormInputState<string, string>;
    public dateEnd: FormInputState<string, string>;
    public redirectType: FormInputState<string, string>;
    public redirectGame: FormInputState<string, string>;
    public redirectCategory: FormInputState<string, string>;
    public redirectUrl: FormInputState<string, string>;
    public termsFull: FormInputState<string, string>;

    public imageDesktop: UploadImageState;
    public imageTablet: UploadImageState;
    public imageMobile: UploadImageState;

    private readonly form: FormModel<FormType>;

    private readonly noEmptyValidationMessage = 'This field is required.';

    constructor(public collection: CollectionType) {
        this.headlineCarouselResource = new Resource(async (): Promise<Array<HeadlineCarouselModelType>> => {
            return await apiCommon.getHeadlineCarousels.run({ carousel_type: this.collection });
        });

        this.title = FormInputState.new('').map(validateNotEmpty(this.noEmptyValidationMessage));
        this.subtitle = FormInputState.new('').map(validateNotEmpty(this.noEmptyValidationMessage));
        this.dateStart = FormInputState.new('').map(validateNotEmpty(this.noEmptyValidationMessage));
        this.dateEnd = FormInputState.new('').map(validateNotEmpty(this.noEmptyValidationMessage));
        this.redirectType = FormInputState.new('NONE');
        this.btnColor = FormInputState.new('').map(validateHexColor('Invalid hex color'));
        this.btnTitle = FormInputState.new('');
        this.bgColor = FormInputState.new('').map(validateHexColor('Invalid hex color'));
        this.redirectGame = FormInputState.new('');
        this.redirectCategory = FormInputState.new('');
        this.redirectUrl = FormInputState.new('').map(validateExpectLink('Invalid redirect url format'));
        this.termsFull = FormInputState.new('');
        this.isPublished = FormInputState.new<boolean>(false);

        this.imageDesktop = new UploadImageState('headline_carousel/desktop_image');
        this.imageTablet = new UploadImageState('headline_carousel/tablet_image');
        this.imageMobile = new UploadImageState('headline_carousel/mobile_image');

        this.form = FormModel.group({
            is_active: this.isPublished,
            title: this.title,
            subtitle: this.subtitle,
            date_from: this.dateStart,
            date_to: this.dateEnd,
            action_template: this.redirectType,
            direct_link: this.redirectUrl,
            game_id: this.redirectGame,
            category_id: this.redirectCategory,
            tc_text: this.termsFull,
            btn_title: this.btnTitle,
            btn_color: this.btnColor,
            bg_color: this.bgColor,
        });
        makeAutoObservable(this);
    }

    @computed private get headlineCarouselsResponse(): HeadlineCarouselContentResponse {
        const headlineCarousels = this.headlineCarouselResource.get();
        if (headlineCarousels.type === 'ready' && headlineCarousels.value !== null) {
            return {
                type: 'ready',
                value: headlineCarousels.value,
            };
        }
        if (headlineCarousels.type === 'loading') {
            return {
                type: 'loading',
            };
        }
        return {
            type: 'error',
            value: 'Something went wrong with fetching carousel',
        };
    }

    @computed public get headlineCarousels(): Array<HeadlineCarouselModelType> {
        if (this.headlineCarouselsResponse.type === 'ready') {
            return this.headlineCarouselsResponse.value.sort((a, b) => a.display_order - b.display_order);
        }
        return [];
    }

    @computed public get activeCarouselItemsLength(): number {
        return this.headlineCarousels.filter((c) => c.is_active).length;
    }

    @action public handleCreate = async (): Promise<void> => {
        this.form.setAsVisited();
        const { result } = this.form;
        if (result.value.type === 'ok') {
            if (this.isRedirectionValid === false) {
                this.redirectionError = 'Redirection option is invalid.';
                await this.wait(5000);
                this.redirectionError = null;
                return;
            }

            if (this.areImagesValid === false) {
                this.imagesError = 'All 3 images are required.';
                await this.wait(5000);
                this.imagesError = null;
                return;
            }

            if (result.value.data.action_template === RedirectTypeEnum.REDIRECT_URL) {
                result.value.data.game_id = null;
            }
            const topElem = this.headlineCarousels.pop();

            const display_order = topElem !== undefined ? topElem.display_order + 1 : 1;

            const headlineCarousel = {
                ...result.value.data,
                game_id: result.value.data.game_id !== null ? result.value.data.game_id.toString() : null,
                category_id: result.value.data.category_id !== null ? result.value.data.category_id.toString() : null,
                img_url_desktop: this.imageDesktop.imgUrl,
                img_url_tablet: this.imageTablet.imgUrl,
                img_url_mobile: this.imageMobile.imgUrl,
                display_order: display_order,
            };

            try {
                const submitResult = await apiCommon.postCreateHeadlineCarousel.run({
                    data: headlineCarousel,
                    collection_slug: this.collection,
                });
                if (submitResult !== null) {
                    await this.headlineCarouselResource.refresh();
                    this.resetForm();
                } else {
                    return this.handleError('Something went wrong.');
                }
            } catch (error) {
                console.error(error);
            }
        }
    };

    @action public setEditHeadline = async (item: HeadlineCarouselType): Promise<void> => {
        this.singleCarousel = item;

        this.title.setValue(item.title ?? '');
        this.subtitle.setValue(item.subtitle ?? '');
        this.btnTitle.setValue(item.btn_title ?? '');
        this.btnColor.setValue(item.btn_color ?? '');
        this.bgColor.setValue(item.bg_color ?? '');
        this.dateStart.setValue(item.date_from ?? '');
        this.dateEnd.setValue(item.date_to ?? '');
        this.redirectType.setValue(item.action_template ?? '');
        this.redirectUrl.setValue(item.direct_link ?? '');
        this.redirectCategory.setValue(item.category_id?.toString() ?? '');
        this.redirectGame.setValue(item.game_id?.toString() ?? '');
        this.termsFull.setValue(item.tc_text ?? '');
        this.imageDesktop.handleChooseImage(item.img_url_desktop ?? '');
        this.imageTablet.handleChooseImage(item.img_url_tablet ?? '');
        this.imageMobile.handleChooseImage(item.img_url_mobile ?? '');
    };

    @action public handleEdit = async (): Promise<void> => {
        this.form.setAsVisited();
        const result = this.form.result;

        if (result.value.type === 'ok' && this.singleCarousel !== null) {
            if (this.isRedirectionValid === false) {
                this.redirectionError = 'Redirection option is invalid.';
                await this.wait(5000);
                this.redirectionError = null;
                return;
            }

            if (this.areImagesValid === false) {
                this.imagesError = 'All 3 images are required.';
                await this.wait(5000);
                this.imagesError = null;
                return;
            }

            if (result.value.data.action_template === RedirectTypeEnum.REDIRECT_GAME) {
                result.value.data.direct_link = '';
            }

            if (result.value.data.action_template === RedirectTypeEnum.REDIRECT_URL) {
                result.value.data.game_id = null;
            }

            const headlineCarousel = {
                ...result.value.data,
                game_id: result.value.data.game_id !== null ? result.value.data.game_id.toString() : null,
                category_id: result.value.data.category_id !== null ? result.value.data.category_id.toString() : null,
                img_url_desktop: this.imageDesktop.imgUrl,
                img_url_tablet: this.imageTablet.imgUrl,
                img_url_mobile: this.imageMobile.imgUrl,
                display_order: this.singleCarousel.display_order,
                is_active: this.singleCarousel.is_active,
            };

            const submitResult = await apiCommon.updateHeadlineCarousel.run({
                data: headlineCarousel,
                id: this.singleCarousel.id,
            });

            if (submitResult !== null) {
                await this.headlineCarouselResource.refresh();
                this.resetForm();
            } else {
                console.error('Error');
            }
        }
    };

    @action public setPublished = async (id: number): Promise<void> => {
        const item = this.headlineCarousels.find((item) => item.id === id);

        if (item !== undefined) {
            const params = {
                data: {
                    ...item,
                    is_active: !item.is_active,
                },
                id: id,
            };

            if (params.data.is_active && this.activeCarouselItemsLength >= 8) {
                this.handleError('There can be max 8 items active! Please unpublish other item.');
                return;
            }

            try {
                await apiCommon.updateHeadlineCarousel.run(params);
                await this.headlineCarouselResource.refresh();
                this.resetForm();
            } catch (error) {
                console.error(error);
            }
        } else {
            console.error('Cannot find the carosuel item with provided ID.');
        }
    };

    @action public setEditMode = (value: boolean): void => {
        this.isEditMode = value;
    };

    @action public deleteCarousel = async (id: number): Promise<void> => {
        const removeResult = await apiCommon.deleteHeadlineCarousel.run({ id });
        if (removeResult) {
            await this.headlineCarouselResource.refresh();
        } else {
            console.error("The item couldn't be deleted.");
        }
    };

    moveItemUp = async (id: number): Promise<void> => {
        const data = {
            id,
            direction: 'up',
        };
        try {
            const result = await apiCommon.changeHeadlineCarouselOrder.run({ data, collection_slug: this.collection });

            if (result) {
                await this.headlineCarouselResource.refresh();
                this.resetForm();
            } else {
                this.handleError('Can not move item up.');
            }
        } catch (error) {
            this.handleError('Can not move item up.');
        }
    };

    moveItemDown = async (id: number): Promise<void> => {
        const data = {
            id,
            collection_slug: this.collection,
            direction: 'down',
        };

        try {
            const result = await apiCommon.changeHeadlineCarouselOrder.run({ data, collection_slug: this.collection });

            if (result) {
                await this.headlineCarouselResource.refresh();
                this.resetForm();
            } else {
                this.handleError('Can not move item down.');
            }
        } catch (error) {
            this.handleError('Can not move item down.');
        }
    };

    @computed public get isLoading(): boolean {
        return this.headlineCarouselsResponse.type === 'loading';
    }

    @computed get isRedirectionValid(): boolean {
        const result = this.form.result;
        if (result.value.type === 'ok') {
            const { action_template, direct_link, game_id, category_id } = result.value.data;
            if (action_template === RedirectTypeEnum.REDIRECT_URL) {
                return direct_link !== '';
            }
            if (action_template === RedirectTypeEnum.REDIRECT_NONE) {
                return true;
            }
            if (action_template === RedirectTypeEnum.REDIRECT_GAME) {
                return game_id !== null && game_id !== '';
            }
            if (
                action_template === RedirectTypeEnum.REDIRECT_CATEGORY_CASINO ||
                action_template === RedirectTypeEnum.REDIRECT_CATEGORY_LIVE_CASINO
            ) {
                return category_id !== null && category_id !== '';
            }
        }
        return false;
    }

    @computed get areImagesValid(): boolean {
        const result = this.form.result;
        if (result.value.type === 'ok') {
            const { imageDesktop, imageTablet, imageMobile } = this;
            if (imageDesktop.imgUrl === '' || imageTablet.imgUrl === '' || imageMobile.imgUrl === '') {
                return false;
            }
        }
        return true;
    }

    public get redirectOptions(): Array<RedirectOptionsType> {
        const redirectionList = [
            {
                key: RedirectTypeEnum.REDIRECT_NONE,
                name: 'None',
            },
            {
                key: RedirectTypeEnum.REDIRECT_URL,
                name: 'Direct link',
            },
            {
                key: RedirectTypeEnum.REDIRECT_GAME,
                name: 'Game',
            },
            {
                key: RedirectTypeEnum.REDIRECT_CATEGORY_CASINO,
                name: 'Casino category',
            },
            {
                key: RedirectTypeEnum.REDIRECT_CATEGORY_LIVE_CASINO,
                name: 'Live casino category',
            },
        ];
        return redirectionList;
    }

    @action public resetForm = (): void => {
        this.form.reset();
        this.isEditMode = false;
        this.singleCarousel = null;
        this.imageDesktop.handleResetImage();
        this.imageTablet.handleResetImage();
        this.imageMobile.handleResetImage();
    };

    @action public handleResetForm = async (): Promise<void> => {
        if (this.isEditMode && this.singleCarousel !== null) {
            await this.setEditHeadline(this.singleCarousel);
        } else {
            this.resetForm();
        }
    };

    @computed public get pageName(): string {
        if (this.collection === 'casino') {
            return 'Casino';
        }
        if (this.collection === 'live-casino') {
            return 'Live Casino';
        }
        return '';
    }

    wait = async (time: number): Promise<void> => {
        return new Promise((resolve) => {
            setTimeout(() => {
                resolve();
            }, time);
        });
    };

    @action handleError = async (message: string): Promise<void> => {
        this.errorMessageForList = message;
        await this.wait(5000);
        this.errorMessageForList = null;
    };

    @action changeCollection = (collection: CollectionType): void => {
        this.collection = collection;
    };

    // --- DATE HANDLERS

    @computed private get parsedStartDate(): number | null {
        return this.dateStart.value !== null ? Date.parse(this.dateStart.value) : null;
    }

    @computed private get parsedEndDate(): number | null {
        return this.dateEnd.value !== null ? Date.parse(this.dateEnd.value) : null;
    }

    @computed public get startDate(): Date | null {
        return this.parsedStartDate === null || isNaN(this.parsedStartDate) ? null : new Date(this.parsedStartDate);
    }

    @computed public get endDate(): Date | null {
        return this.parsedEndDate === null || isNaN(this.parsedEndDate) ? null : new Date(this.parsedEndDate);
    }
}
